import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import SmartTable, { SmartTableColumn } from '../../../shared/components/SmartTable';
import SmartTableButtons from '../../../shared/components/SmartTableButtons';
import LayoutWrapper from '../../../shared/components/LayoutWrapper';
import { useAuth } from '../../../shared/hooks/useAuth';
import { API_CONFIG } from '../../../config/api';
import { getTokenInfo, logTokenInfo, setupTokens } from '../../../config/novaqToken';
import { handleTaxReportSubmission, TaxSubmissionStep } from '../lib/TaxSendHHOAT';


// SalarySheet мэдээлэл авах API функц
export const fetchSalarySheetData = async (customerEmployoID?: string, year?: string, quarter?: string, customerOid?: string): Promise<any> => {
  try {
    console.log('🔍 Fetching SalarySheet data...');
    
    // Build query parameters
    const queryParams = new URLSearchParams();
    if (customerEmployoID) queryParams.append('customerEmployoID', customerEmployoID);
    if (customerOid) queryParams.append('customerOid', customerOid);
    if (year) queryParams.append('year', year);
    if (quarter) queryParams.append('quarter', quarter);
    
    const url = `${API_CONFIG.BASE_URL}/api/tax-post-hhoat/salary-sheet${queryParams.toString() ? '?' + queryParams.toString() : ''}`;
    console.log('📊 Fetch URL:', url);
    console.log('📊 Parameters:', { customerEmployoID, customerOid, year, quarter });
    
    const response = await fetch(url, {
      method: 'GET',
      headers: {
        'Authorization': `Bearer ${localStorage.getItem('novaqToken')}`,
        'Content-Type': 'application/json'
      }
    });
    
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    
    const data = await response.json();
    console.log('✅ SalarySheet response:', data);
    
    return data;
  } catch (error) {
    console.error('❌ Error getting SalarySheet data:', error);
    throw error;
  }
};

// Customers хүснэгтээс Oid болон entId авах API функц
const fetchCustomersData = async (): Promise<any> => {
  try {
    console.log('🔍 Fetching Customers data...');
    
    const url = `${API_CONFIG.BASE_URL}/api/customerslist/accountant/customers`;
    console.log('📊 Fetch URL:', url);
    
    const response = await fetch(url, {
      method: 'GET',
      headers: {
        'Authorization': `Bearer ${localStorage.getItem('novaqToken')}`,
        'Content-Type': 'application/json'
      }
    });
    
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    
    const data = await response.json();
    console.log('✅ Customers response:', data);
    
    return data;
  } catch (error) {
    console.error('❌ Error getting Customers data:', error);
    throw error;
  }
};

// MoveIncomeExpenseToHHOAT процедурыг дуудах API функц
export const executeMoveIncomeExpense = async (customerOid: string, reportNo: string, taxYear: number, taxPeriod: number, formNo?: string): Promise<any> => {
  try {
    console.log('🔍 Executing MoveIncomeExpenseToHHOAT procedure...');
    console.log('📊 Parameters:', { customerOid, reportNo, taxYear, taxPeriod, formNo });
    
    const response = await fetch(`${API_CONFIG.BASE_URL}/api/tax-post-hhoat/move-income-expense`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${localStorage.getItem('novaqToken')}`,
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({
        customerOid,
        reportNo,
        taxYear,
        taxPeriod,
        formNo: formNo || '1251'
      })
    });
    
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    
    const data = await response.json();
    console.log('✅ MoveIncomeExpenseToHHOAT response:', data);
    
    return data;
  } catch (error) {
    console.error('❌ Error executing MoveIncomeExpenseToHHOAT procedure:', error);
    throw error;
  }
};


const CustomersTaxInvoiceList: React.FC = () => {
  const { userAuth } = useAuth();
  const navigate = useNavigate();
  
  // State management
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [salarySheetData, setSalarySheetData] = useState<any[]>([]);
  const [showColumnSearch, setShowColumnSearch] = useState(false);
  const [showColumnSettings, setShowColumnSettings] = useState(false);
  
  // Tax report submission states
  const [submittingReport, setSubmittingReport] = useState(false);
  const [submissionSteps, setSubmissionSteps] = useState<TaxSubmissionStep[]>([]);
  const [showSubmissionProgress, setShowSubmissionProgress] = useState(false);
  
  // Customer selection states
  const [customers, setCustomers] = useState<any[]>([]);
  const [selectedCustomer, setSelectedCustomer] = useState<string>('');
  const [showCustomerSelection, setShowCustomerSelection] = useState(false);
  
  // Filter states
  const [selectedYear, setSelectedYear] = useState<string>('2025');
  const [selectedQuarter, setSelectedQuarter] = useState<string>('1');
  
  // HHOAT procedure parameters
  const [customerOid, setCustomerOid] = useState<string>('');
  const [reportNo, setReportNo] = useState<string>('0'); // Default report number
  const [formNo, setFormNo] = useState<string>('1251'); // Default form number
  
  // Available tax form types
  const taxFormTypes = [
    { value: '1251', label: 'Цалин, хөдөлмөрийн хөлс (1251)' },
    { value: '1119', label: 'Суутган Хувь хүн (1119)' },
    { value: '1130', label: 'Суутган ААН (1130)' }
  ];
  
  


  // Button handlers
  const handleToggleSearch = () => {
    setShowColumnSearch(prev => !prev);
  };

  const handleColumnSettings = () => {
    setShowColumnSettings(prev => !prev);
  };

  const handleRefresh = async () => {
    try {
      console.log('🔄 Тайлан дуудах товч дарагдаж, MoveIncomeExpenseToHHOAT процедур ажиллуулж байна...');
      setLoading(true);
      setError(null);
      
      // MoveIncomeExpenseToHHOAT процедурыг дуудах
      const taxYear = parseInt(selectedYear);
      const taxPeriod = parseInt(selectedQuarter);
      
      console.log('📊 HHOAT procedure parameters:', {
        customerOid: selectedCustomer || customerOid,
        reportNo,
        taxYear,
        taxPeriod,
        formNo
      });
      
      const procedureResult = await executeMoveIncomeExpense(
        selectedCustomer || customerOid,
        reportNo,
        taxYear,
        taxPeriod,
        formNo
      );
      
      if (procedureResult.success) {
        console.log('✅ MoveIncomeExpenseToHHOAT procedure executed successfully');
        
        // Процедур амжилттай дуудагдаж дууссаны дараа өгөгдөл дахин ачаалах
        console.log('🔄 Procedure дууссан, SalarySheet мэдээлэл дахин ачаалж байна...');
        await loadSalarySheetData();
        
        // Амжилттай гэсэн мэдээлэл харуулах
        console.log('✅ Тайлан амжилттай дуудагдаж, өгөгдөл шинэчлэгдлээ');
      } else {
        console.error('❌ MoveIncomeExpenseToHHOAT procedure failed:', procedureResult.message);
        setError('Тайлан дуудахад алдаа гарлаа: ' + procedureResult.message);
      }
      
    } catch (error) {
      console.error('❌ Error in handleRefresh:', error);
      setError('Тайлан дуудахад алдаа гарлаа: ' + (error as Error).message);
    } finally {
      setLoading(false);
    }
  };

  const handleSendReport = async () => {
    try {
      // Цалингийн мэдээлэл байхгүй бол X-ээр тайлан илгээгдэнэ
      // if (!salarySheetData || salarySheetData.length === 0) {
      //   alert('Тайлангийн өгөгдөл байхгүй байна. Эхлээд "Тайлан дуудах" товчийг дарна уу.');
      //   return;
      // }

      // AccountantOid шалгах
      if (!userAuth?.accountantOid) {
        alert('Нягтлан бодогчийн мэдээлэл олдсонгүй. Дахин нэвтрэнэ үү.');
        return;
      }

      // Customer сонгосон эсэхийг шалгах
      if (!selectedCustomer) {
        alert('Customer сонгоно уу.');
        return;
      }

      // Сонгосон customer-ийн тайланг илгээх
      await handleCustomerSelection(selectedCustomer);
      
    } catch (error) {
      console.error('❌ Error in handleSendReport:', error);
      setError('Тайлан илгээх явцад алдаа гарлаа: ' + (error as Error).message);
    }
  };

  const handleCustomerSelection = async (customerEntId: string) => {
    try {
      setSubmittingReport(true);
      setError(null);
      setSubmissionSteps([]);
      setShowSubmissionProgress(true);
      
      console.log('📤 Tax HHOAT Report илгээх эхэлж байна...');
      console.log('📊 Report data:', salarySheetData);
      console.log('📊 Parameters:', {
        year: parseInt(selectedYear),
        quarter: `${selectedQuarter}-р улирал`,
        accountantOid: userAuth?.accountantOid,
        customerEntId: customerEntId
      });
      
      // Жил болон улирлын мэдээлэл ашиглан formData үүсгэх
      const formData = generateFormData(parseInt(selectedYear), parseInt(selectedQuarter));
      
      // Сонгосон customer-ийн тайланг илгээх
      console.log(`📤 Customer ${customerEntId} тайланг илгээж байна...`);
      
      try {
        // Tax HHOAT Report submission эхлүүлэх
        await handleTaxReportSubmission(
          parseInt(selectedYear),
          `${selectedQuarter}-р улирал`,
          userAuth?.accountantOid || '',
          customerEntId, // Сонгосон customer-ийн entId
          formData, // formData нэмэх
          (step: TaxSubmissionStep) => {
            console.log(`📊 Customer ${customerEntId} submission step update:`, step);
            setSubmissionSteps(prev => {
              const newSteps = [...prev];
              const existingStepIndex = newSteps.findIndex(s => s.step === step.step);
              if (existingStepIndex >= 0) {
                newSteps[existingStepIndex] = step;
              } else {
                newSteps.push(step);
              }
              return newSteps.sort((a, b) => a.step - b.step);
            });
          },
          (result) => {
            console.log(`✅ Customer ${customerEntId} Tax HHOAT Report submission амжилттай дууслаа:`, result);
            
            // Амжилттай дууссан мэдээлэл харуулах
            const summaryMessage = `Tax HHOAT Report амжилттай илгээгдлээ!\n\n` +
              `Жил: ${selectedYear}\n` +
              `Улирал: ${selectedQuarter}-р улирал\n` +
              `Customer: ${customerEntId}\n` +
              `Илгээсэн цаг: ${new Date().toLocaleString('mn-MN')}`;
            
            alert(summaryMessage);
          },
          (error) => {
            console.error(`❌ Customer ${customerEntId} Tax HHOAT Report submission алдаа:`, error);
            alert(`Тайлан илгээхэд алдаа гарлаа: ${error.message}`);
          }
        );
        
        console.log(`✅ Customer ${customerEntId} тайлан амжилттай илгээгдлээ`);
        
      } catch (error) {
        console.error(`❌ Customer ${customerEntId} тайлан илгээх алдаа:`, error);
        alert(`Тайлан илгээхэд алдаа гарлаа: ${(error as Error).message}`);
      }
      
      // Progress modal хаах
      setShowSubmissionProgress(false);
      setSubmissionSteps([]);
      
    } catch (error) {
      console.error('❌ Error in handleCustomerSelection:', error);
      setError('Тайлан илгээх явцад алдаа гарлаа: ' + (error as Error).message);
      setShowSubmissionProgress(false);
      setSubmissionSteps([]);
    } finally {
      setSubmittingReport(false);
    }
  };

  // Filter handlers
  const handleYearChange = (year: string) => {
    setSelectedYear(year);
    console.log('📅 Жил солигдлоо:', year);
    
    // Жил солигдсон бол тайланг дахин ачаалах
    if (selectedCustomer) {
      console.log('📊 Жил солигдсон, customer filter-тэй тайланг дахин ачаалж байна');
      loadSalarySheetDataWithCustomer(selectedCustomer);
    } else {
      console.log('📊 Жил солигдсон, бүх тайланг дахин ачаалж байна');
      loadSalarySheetData();
    }
  };

  const handleQuarterChange = (quarter: string) => {
    setSelectedQuarter(quarter);
    console.log('📅 Улирал солигдлоо:', quarter);
    
    // Улирлын дагуу саруудыг тооцоолох
    const quarterMonths = getQuarterMonths(quarter);
    console.log('📅 Улирлын сарууд:', quarterMonths);
    
    // Улирал солигдсон бол тайланг дахин ачаалах
    if (selectedCustomer) {
      console.log('📊 Улирал солигдсон, customer filter-тэй тайланг дахин ачаалж байна');
      loadSalarySheetDataWithCustomer(selectedCustomer);
    } else {
      console.log('📊 Улирал солигдсон, бүх тайланг дахин ачаалж байна');
      loadSalarySheetData();
    }
  };

  // Улирлын дагуу саруудыг тооцоолох функц (жилийн эхнээс хойшхи нийлбэр)
  const getQuarterMonths = (quarter: string): number[] => {
    switch (quarter) {
      case '1': return [1, 2, 3];                    // 1-р улирал: 1, 2, 3 сар
      case '2': return [1, 2, 3, 4, 5, 6];          // 2-р улирал: 1-6 сар (жилийн эхнээс)
      case '3': return [1, 2, 3, 4, 5, 6, 7, 8, 9]; // 3-р улирал: 1-9 сар (жилийн эхнээс)
      case '4': return [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12]; // 4-р улирал: 1-12 сар (жилийн эхнээс)
      default: return [1, 2, 3];
    }
  };

  // Жил болон улирлын мэдээлэл ашиглан formData үүсгэх функц
  const generateFormData = (year: number, quarter: number) => {
    console.log('📋 FormData үүсгэж байна:', { year, quarter });
    
    // Mock data устгаад хоосон formData буцаах - backend дээр database-ээс мэдээлэл авна
    return {};
  };

  // Жилийн сонголтууд (2020-2030)
  const yearOptions = Array.from({ length: 11 }, (_, i) => (2020 + i).toString());

  // Улирлын сонголтууд
  const quarterOptions = [
    { value: '1', label: '1-р улирал' },
    { value: '2', label: '2-р улирал' },
    { value: '3', label: '3-р улирал' },
    { value: '4', label: '4-р улирал' }
  ];

  // SalarySheet мэдээлэл ачаалах функц
  const loadSalarySheetData = async () => {
    try {
      console.log('🔄 Loading SalarySheet data...');
      console.log('📊 Filter parameters:', { selectedYear, selectedQuarter, selectedCustomer });
      setLoading(true);
      setError(null);
      
      const result = await fetchSalarySheetData(
        selectedCustomer || undefined,
        selectedYear || undefined,
        selectedQuarter || undefined,
        customerOid || undefined
      );
      
      if (result.success && result.data) {
        console.log('✅ SalarySheet data loaded successfully:', result.data.length, 'records');
        setSalarySheetData(result.data);
        // If backend returns resolvedCustomerOid or data rows include customerOid, set it for procedure calls
        if (result.resolvedCustomerOid) {
          setCustomerOid(result.resolvedCustomerOid);
        } else if (result.data.length > 0 && result.data[0].customerOid) {
          setCustomerOid(result.data[0].customerOid);
        }
      } else {
        console.log('⚠️ No SalarySheet data found');
        setSalarySheetData([]);
        setCustomerOid('');
      }
      
    } catch (error) {
      console.error('❌ Error loading SalarySheet data:', error);
      setError('SalarySheet мэдээлэл авахад алдаа гарлаа: ' + (error as Error).message);
    } finally {
      setLoading(false);
    }
  };

  // Customer-аар шүүсэн SalarySheet мэдээлэл ачаалах функц
  const loadSalarySheetDataWithCustomer = async (customerOid: string) => {
    try {
      console.log('🔄 Loading SalarySheet data with customer filter...');
      console.log('📊 Filter parameters:', { selectedYear, selectedQuarter, customerOid });
      setLoading(true);
      setError(null);
      
      const result = await fetchSalarySheetData(
        customerOid,
        selectedYear || undefined,
        selectedQuarter || undefined,
        customerOid
      );
      
      if (result.success && result.data) {
        console.log('✅ SalarySheet data with customer filter loaded successfully:', result.data.length, 'records');
        setSalarySheetData(result.data);
        if (result.resolvedCustomerOid) {
          setCustomerOid(result.resolvedCustomerOid);
        } else if (result.data.length > 0 && result.data[0].customerOid) {
          setCustomerOid(result.data[0].customerOid);
        }
      } else {
        console.log('⚠️ No SalarySheet data found for selected customer');
        setSalarySheetData([]);
        setCustomerOid('');
      }
      
    } catch (error) {
      console.error('❌ Error loading SalarySheet data with customer filter:', error);
      setError('Customer-аар шүүсэн SalarySheet мэдээлэл авахад алдаа гарлаа: ' + (error as Error).message);
    } finally {
      setLoading(false);
    }
  };

  // Load customers data
  const loadCustomersData = async () => {
    try {
      console.log('🔍 Loading customers data...');
      const customersResponse = await fetchCustomersData();
      
      if (customersResponse && customersResponse.data && customersResponse.data.length > 0) {
        console.log('✅ Customers data loaded successfully:', customersResponse.data.length, 'customers');
        setCustomers(customersResponse.data);
      } else {
        console.log('⚠️ No customers data found');
        setCustomers([]);
      }
    } catch (error) {
      console.error('❌ Error loading customers data:', error);
      setError('Customers мэдээлэл авахад алдаа гарлаа: ' + (error as Error).message);
    }
  };

  // Load token from localStorage on component mount
  useEffect(() => {
    console.log('🔍 Loading token from localStorage...');
    
    // Token мэдээлэл авах
    const tokenInfo = getTokenInfo();
    logTokenInfo(tokenInfo);
    
    if (tokenInfo.hasValidToken) {
      console.log('✅ Token loaded successfully');
      
      // TaxToken-г global-д тохируулах
      setupTokens(tokenInfo);
      
      // Load SalarySheet data
      console.log('🔄 Loading SalarySheet data...');
      loadSalarySheetData();
      
      // Load customers data
      console.log('🔄 Loading customers data...');
      loadCustomersData();
    } else {
      console.log('❌ No valid token found');
      setError('Хэрэглэгч нэвтрээгүй эсвэл token олдсонгүй. Дахин нэвтрэнэ үү.');
      // Redirect to home page
      console.log('🔄 Redirecting to home page...');
      navigate('/');
    }
  }, [navigate]);

  const defaultColumns: SmartTableColumn[] = [  
    { key: 'customerId', label: 'Харилцагчийн регистр', width: 150 },
    { key: 'customerName', label: 'Харилцагчийн нэр', width: 200 },
    { key: 'employeeCode', label: 'Ажилтны регистр', width: 150 },
    { key: 'employeeLastName', label: 'Ажилтны овог', width: 150 },
    { key: 'employeeName', label: 'Ажилтны нэр', width: 150 },
    { key: 'Year', label: 'Жил', width: 80, render: (value) => (
      <div className="text-center font-medium text-blue-600">
        {value}
      </div>
    )},
    { key: 'SalaryRecordCount', label: 'Цалингийн тоо', width: 120, render: (value) => (
      <div className="text-center font-medium text-gray-600">
        {value} удаа
      </div>
    )},
    
    // Жилийн нийлбэр дүнүүд
    { key: 'TotalYearSalary', label: 'Жилийн нийт цалин', width: 180, render: (value) => (
      <div className="font-bold text-green-600 bg-green-50 px-2 py-1 rounded">
        {(value || 0).toLocaleString('mn-MN')} ₮
      </div>
    )},
    { key: 'TotalInsureEmployeeAmount', label: 'НДШ ажилтны дүн', width: 180, render: (value) => (
      <div className="font-medium text-yellow-600">
        {(value || 0).toLocaleString('mn-MN')} ₮
      </div>
    )},
    { key: 'TotalInsureEmployerAmount', label: 'НДШ ажил олгогчийн дүн', width: 200, render: (value) => (
      <div className="font-medium text-purple-600">
        {(value || 0).toLocaleString('mn-MN')} ₮
      </div>
    )},
    { key: 'TotalTaxAmount', label: 'Ноогдох татвар', width: 150, render: (value) => (
      <div className="font-medium text-red-600">
        {(value || 0).toLocaleString('mn-MN')} ₮
      </div>
    )},
    { key: 'TotalPITaxAmount', label: 'ХОАТ-ын дүн', width: 150, render: (value) => (
      <div className="font-medium text-indigo-600">
        {(value || 0).toLocaleString('mn-MN')} ₮
      </div>
    )},
    
    // Өссөн дүнгийн баганууд (жилийн нийлбэр)
    { key: 'TotalYearSalaryWithBenefits', label: 'Жилийн өссөн нийт дүн', width: 200, render: (value) => (
      <div className="font-bold text-green-700 bg-green-100 px-3 py-2 rounded-lg border-2 border-green-300">
        {(value || 0).toLocaleString('mn-MN')} ₮
      </div>
    )},
    { key: 'TotalYearInsurance', label: 'Жилийн НДШ нийт дүн', width: 200, render: (value) => (
      <div className="font-medium text-purple-700 bg-purple-50 px-2 py-1 rounded">
        {(value || 0).toLocaleString('mn-MN')} ₮
      </div>
    )},
    { key: 'TotalYearTax', label: 'Жилийн татварын нийт дүн', width: 200, render: (value) => (
      <div className="font-medium text-red-700 bg-red-50 px-2 py-1 rounded">
        {(value || 0).toLocaleString('mn-MN')} ₮
      </div>
    )},

    
    
  
   
    
  ];

  // Debug log for render
  console.log('🔍 TaxPostHHOAT render - salarySheetData:', salarySheetData.length);

  return (
    <LayoutWrapper userAuth={userAuth}>
      <div className="p-6">
        {/* Header */}
        <div className="flex justify-between items-center mb-6">
          <h1 className="text-2xl font-bold text-gray-800">Цалингийн жагсаалт</h1>
          
          {/* Action Buttons */}
          <SmartTableButtons
            showColumnSearch={showColumnSearch}
            onToggleSearch={handleToggleSearch}
            onColumnSettings={handleColumnSettings}
            onRefresh={handleRefresh}
            addable={false}
          />
        </div>

        {/* Filter Section */}
        <div className="bg-white rounded-lg shadow-sm border mb-6 p-4">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-6 gap-4 items-end">
            {/* 1. Year Filter */}
            <div className="flex flex-col">
              <label className="text-sm font-medium text-gray-700 mb-1">1. Жил сонгох</label>
              <select
                value={selectedYear}
                onChange={(e) => handleYearChange(e.target.value)}
                className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                {yearOptions.map(year => (
                  <option key={year} value={year}>{year}</option>
                ))}
              </select>
            </div>

            {/* 2. Quarter Filter */}
            <div className="flex flex-col">
              <label className="text-sm font-medium text-gray-700 mb-1">2. Улирал сонгох</label>
              <select
                value={selectedQuarter}
                onChange={(e) => handleQuarterChange(e.target.value)}
                className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                {quarterOptions.map(quarter => (
                  <option key={quarter.value} value={quarter.value}>{quarter.label}</option>
                ))}
              </select>
            </div>

            {/* 3. Customer Selection */}
            <div className="flex flex-col">
              <label className="text-sm font-medium text-gray-700 mb-1">3. Харилцагч сонгох</label>
              <select
                value={selectedCustomer}
                onChange={(e) => {
                  setSelectedCustomer(e.target.value);
                  // Customer сонгосны дараа тайланг дахин ачаалах
                  if (e.target.value) {
                    console.log('📊 Customer солигдлоо, тайланг дахин ачаалж байна:', e.target.value);
                    loadSalarySheetDataWithCustomer(e.target.value);
                  } else {
                    console.log('📊 Customer filter арилгагдлаа, бүх тайланг харуулж байна');
                    loadSalarySheetData();
                  }
                }}
                className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                <option value="">Бүх харилцагч</option>
                {customers.map((customer, index) => (
                  <option key={`customer-option-${index}-${customer.entId}`} value={customer.Oid}>
                    {customer.CustomerName || 'Нэр олдсонгүй'} ({customer.entId || 'N/A'})
                  </option>
                ))}
              </select>
            </div>

            {/* 4. Form Number Selection */}
            <div className="flex flex-col">
              <label className="text-sm font-medium text-gray-700 mb-1">4. Тайлангийн төрөл</label>
              <select
                value={formNo}
                onChange={(e) => {
                  setFormNo(e.target.value);
                  console.log('📊 FormNo солигдлоо:', e.target.value);
                }}
                className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                {taxFormTypes.map(formType => (
                  <option key={formType.value} value={formType.value}>
                    {formType.label}
                  </option>
                ))}
              </select>
            </div>

            {/* 5. Call Report Button */}
            <div className="flex flex-col">
              <label className="text-sm font-medium text-gray-700 mb-1">5. Тайлан дуудах</label>
              <button
                onClick={handleRefresh}
                disabled={loading}
                className="px-6 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-green-500 focus:ring-offset-2 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {loading ? 'Ажиллаж байна...' : 'Тайлан дуудах'}
              </button>
            </div>

            {/* 6. Send Report Button */}
            <div className="flex flex-col">
              <label className="text-sm font-medium text-gray-700 mb-1">6. Илгээх</label>
              <button
                onClick={handleSendReport}
                disabled={loading || submittingReport || !selectedCustomer}
                className="px-6 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {submittingReport ? 'Илгээж байна...' : loading ? 'Ажиллаж байна...' : 'Тайлан илгээх'}
              </button>
            </div>
          </div>
        </div>

        {/* Data Table */}
        <div className="bg-white rounded-lg shadow-sm border mb-6">
          <div className="p-6">
            <SmartTable
              data={salarySheetData}
              columns={defaultColumns}
              fetchData={() => {
                console.log('🔄 SmartTable fetchData called');
                console.log('🔄 Calling loadSalarySheetData from fetchData');
                loadSalarySheetData();
              }}
              settingsKey="salary-sheet-v2"
              rowKey="CustomerEmployeeId"
              editable={false}
              showColumnSearch={showColumnSearch}
              showColumnSettings={showColumnSettings}
            />
          </div>
        </div>

        {/* Customer Selection Modal */}
        {showCustomerSelection && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-lg p-6 max-w-2xl w-full mx-4">
              <h3 className="text-lg font-semibold text-gray-800 mb-4">
                Customer сонгох
              </h3>
              
              <div className="space-y-3 max-h-96 overflow-y-auto">
                {customers.map((customer, index) => (
                  <div key={`customer-modal-${index}-${customer.entId}`} className="flex items-center justify-between p-3 border border-gray-200 rounded-lg hover:bg-gray-50">
                    <div className="flex-1">
                      <div className="font-medium text-gray-800">
                        {customer.CustomerName || 'Нэр олдсонгүй'}
                      </div>
                      <div className="text-sm text-gray-600">
                        ID: {customer.entId || 'N/A'} | OID: {customer.Oid || 'N/A'}
                      </div>
                    </div>
                    
                    <button
                      onClick={() => handleCustomerSelection(customer.entId)}
                      className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
                    >
                      Сонгох
                    </button>
                  </div>
                ))}
              </div>
              
              <div className="mt-6 flex justify-end">
                <button
                  onClick={() => setShowCustomerSelection(false)}
                  className="px-4 py-2 bg-gray-500 text-white rounded-md hover:bg-gray-600 focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2"
                >
                  Хаах
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Submission Progress Modal */}
        {showSubmissionProgress && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-lg p-6 max-w-2xl w-full mx-4">
              <h3 className="text-lg font-semibold text-gray-800 mb-4">
                Tax HHOAT Report илгээж байна...
              </h3>
              
              <div className="space-y-3">
                {submissionSteps.map((step, index) => (
                  <div key={step.step} className="flex items-center space-x-3">
                    <div className={`w-6 h-6 rounded-full flex items-center justify-center text-sm font-medium ${
                      step.status === 'completed' ? 'bg-green-500 text-white' :
                      step.status === 'running' ? 'bg-blue-500 text-white animate-pulse' :
                      step.status === 'failed' ? 'bg-red-500 text-white' :
                      'bg-gray-300 text-gray-600'
                    }`}>
                      {step.status === 'completed' ? '✓' :
                       step.status === 'failed' ? '✗' :
                       step.status === 'running' ? '⏳' :
                       step.step}
                    </div>
                    
                    <div className="flex-1">
                      <div className={`text-sm font-medium ${
                        step.status === 'completed' ? 'text-green-700' :
                        step.status === 'running' ? 'text-blue-700' :
                        step.status === 'failed' ? 'text-red-700' :
                        'text-gray-600'
                      }`}>
                        {step.step}. {step.name}
                      </div>
                      
                      {step.message && (
                        <div className={`text-xs ${
                          step.status === 'completed' ? 'text-green-600' :
                          step.status === 'running' ? 'text-blue-600' :
                          step.status === 'failed' ? 'text-red-600' :
                          'text-gray-500'
                        }`}>
                          {step.message}
                        </div>
                      )}
                      
                      {step.error && (
                        <div className="text-xs text-red-600 bg-red-50 p-2 rounded mt-1">
                          Алдаа: {step.error}
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
              
              <div className="mt-6 flex justify-end">
                <button
                  onClick={() => setShowSubmissionProgress(false)}
                  className="px-4 py-2 bg-gray-500 text-white rounded-md hover:bg-gray-600 focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2"
                  disabled={submittingReport}
                >
                  {submittingReport ? 'Ажиллаж байна...' : 'Хаах'}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Error Messages */}
        {error && (
          <div className="fixed top-4 right-4 z-50">
            <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded">
              {error}
            </div>
          </div>
        )}
      </div>
    </LayoutWrapper>
  );
};

export default CustomersTaxInvoiceList;
